import React, { useState, useEffect } from 'react';
import { onSnapshot, doc } from "firebase/firestore";
import { db, setUsernameCallable } from '../firebase/config';
import { formatDate } from '../utils/helpers';

const ProfileScreen = ({ user, setScreen }) => {
    const [userProfile, setUserProfile] = useState(null);
    const [usernameInput, setUsernameInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);

    useEffect(() => {
      if (!user) return;
  
      const userDocRef = doc(db, 'users', user.uid);
      const unsubscribe = onSnapshot(userDocRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          setUserProfile(data);
          setUsernameInput(data.username || '');
        } else {
          console.log("No such user profile!");
        }
      });
  
      return () => unsubscribe();
    }, [user]);

    const handleUsernameChange = (e) => {
      const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
      setUsernameInput(value);
      setMessage('');
    };

    const handleSaveUsername = async () => {
      if (usernameInput === (userProfile?.username || '')) {
        setMessage('Username is unchanged.');
        setIsError(false);
        return;
      }
  
      setIsLoading(true);
      setMessage('');
      setIsError(false);
  
      try {
        const result = await setUsernameCallable({ username: usernameInput });
        setMessage(result.data.message);
        setIsError(false);
      } catch (error) {
        console.error("Error setting username:", error);
        setMessage(error.message);
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };
    
    const memberSince = userProfile?.createdAt ? formatDate(userProfile.createdAt.toDate()) : '...';

    return (
        <div className="animate-fade-in">

            <header className="flex justify-between items-center mb-8">
              <h1 className="text-4xl font-bold text-[#D4AF37] font-serif">Your Profile</h1>
              <button onClick={() => setScreen('dashboard')} className="bg-[#4E9A91] text-white font-bold py-2 px-4 rounded-lg hover:bg-teal-700 transition-colors duration-300">
                &larr; Back to Dashboard
              </button>
            </header>

            <div className="bg-[#1E1E1E]/50 p-8 rounded-lg border border-gray-800">
              <div className="mb-6">
                <label className="block text-sm font-bold mb-2 text-gray-400">Email</label>
                <p className="text-lg">{user.email}</p>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-bold mb-2 text-gray-400">Member Since</label>
                <p className="text-lg">{memberSince}</p>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-bold mb-2 text-gray-400">Subscription</label>
                <p className="text-lg capitalize">{userProfile?.subscriptionStatus || 'Free'}</p>
              </div>

              <div className="mb-4">
                <label htmlFor="username" className="block text-sm font-bold mb-2 text-gray-400">
                  Username
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  3-20 characters, lowercase letters, numbers, and underscores only.
                </p>
                <div className="flex items-center">
                  <span className="text-gray-500 mr-1">@</span>
                  <input
                    id="username"
                    type="text"
                    value={usernameInput}
                    onChange={handleUsernameChange}
                    className="bg-gray-800 border border-gray-700 rounded w-full py-2 px-3 leading-tight focus:outline-none focus:border-[#D4AF37]"
                    placeholder="your_username"
                    maxLength="20"
                  />
                </div>
              </div>

              <button
                onClick={handleSaveUsername}
                disabled={isLoading || usernameInput.length < 3 || usernameInput === userProfile?.username}
                className="bg-[#D4AF37] text-gray-900 font-bold py-2 px-4 rounded-lg hover:bg-yellow-600 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors duration-300"
              >
                {isLoading ? 'Saving...' : 'Save Username'}
              </button>

              {message && (
                <p className={`mt-4 text-sm ${isError ? 'text-red-400' : 'text-green-400'}`}>{message}</p>
              )}
            </div>
        </div>
    );
};

export default ProfileScreen;
